import { Helmet } from 'react-helmet-async';

interface SocialMetaTagsProps {
  title: string;
  description: string;
  url: string;
  image?: string;
  imageAlt?: string;
  type?: 'website' | 'article';
  twitterCard?: 'summary' | 'summary_large_image';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
  tags?: string[];
}

/**
 * Enhanced social media meta tags component
 * Optimizes Open Graph and Twitter Card metadata for better social sharing
 */
export const SocialMetaTags = ({
  title,
  description,
  url,
  image = "https://flatjive.com/placeholder.svg",
  imageAlt = "FlatJive - Property Management Platform",
  type = 'website',
  twitterCard = 'summary_large_image',
  publishedTime,
  modifiedTime,
  author,
  section,
  tags
}: SocialMetaTagsProps) => {
  // Get canonical base URL from environment
  const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://flatjive.com';
  
  // Ensure URL is absolute
  const absoluteUrl = url.startsWith('http') ? url : `${SITE_URL}${url}`;
  
  // Ensure image URL is absolute
  const absoluteImageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;

  return (
    <Helmet>
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={absoluteUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImageUrl} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="FlatJive" />
      <meta property="og:locale" content="en_GB" />
      
      {/* Article specific Open Graph tags */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      {type === 'article' && section && (
        <meta property="article:section" content={section} />
      )}
      {type === 'article' && tags && tags.map((tag, index) => (
        <meta key={index} property="article:tag" content={tag} />
      ))}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={absoluteUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImageUrl} />
      <meta name="twitter:image:alt" content={imageAlt} />
      <meta name="twitter:site" content="@flatjive" />
      <meta name="twitter:creator" content="@flatjive" />
      
      {/* Additional Twitter tags for better display */}
      {type === 'article' && (
        <>
          <meta name="twitter:label1" content="Reading time" />
          <meta name="twitter:data1" content="5 min read" />
        </>
      )}
    </Helmet>
  );
};
